import React, { useContext } from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  makeStyles,
  Theme,
  createStyles,
  Paper,
} from '@material-ui/core';
import mainColors from '../../../themes/main-colors';
import {
  BusinessNeedsCategory,
  CommercialRetailOfficeOption,
  ConsultationType,
  EnergyConsultationDetails,
  LargeCommercialIndustrialOption,
} from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import EmailIcon from '../NewConsultationForm/icons/EmailIcon';
import PhoneIcon from '../NewConsultationForm/icons/PhoneIcon';
import SiteVisitIcon from '../NewConsultationForm/icons/SiteVisitIcon';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  LanguageContext,
  SupportedLanguages,
} from '../../../providers/LanguageProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      border: `1px solid ${mainColors.lightGray8}`,
      borderRadius: 8,
      padding: theme.spacing(2, 3),
      marginBottom: theme.spacing(2),
      backgroundColor: mainColors.white,
      borderLeftColor: mainColors.sparkBlue,
      borderLeftWidth: '5px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    iconContainer: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      backgroundColor: mainColors.riverBlue,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: theme.spacing(2),

      '& svg': {
        width: 16,
        height: 16,
      },
    },
    title: {
      fontWeight: 600,
      fontSize: 20,
      color: mainColors.noirBlur,
    },
    label: {
      fontSize: 11,
      color: mainColors.grey4,
      textTransform: 'uppercase',
    },
    value: {
      fontSize: 15,
      color: '#333',
    },
    row: {
      display: 'flex',
      gap: '24px',
      marginBottom: theme.spacing(2),
    },
    energyNeeds: {
      marginTop: theme.spacing(1),
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
      marginTop: theme.spacing(1),

      '& > *': {
        marginLeft: theme.spacing(1),
      },
    },
    rescheduleBtn: {
      backgroundColor: mainColors.white,
      border: `1px solid ${mainColors.sparkBlue}`,
      color: mainColors.sparkBlue,
      textTransform: 'none',
      minWidth: 140,
    },

    cancelBtn: {
      backgroundColor: mainColors.white,
      border: `1px solid ${mainColors.accentRed}`,
      color: mainColors.accentRed,
      textTransform: 'none',
      minWidth: 140,

      '&:hover': {
        backgroundColor: mainColors.white,
      },
    },
  }),
);

interface Props {
  consultation: EnergyConsultationDetails;
  onReschedule?: (id: string) => void;
  onCancel?: () => void;
}

const ConsultationRequestCard: React.FC<Props> = ({
  consultation,
  onReschedule,
  onCancel,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const businessNeedsCategory =
    consultation?.requestDetails?.businessNeedsCategory ===
    BusinessNeedsCategory.CommercialRetailOffice
      ? t('CONSULTATION_TYPE_COMMERCIAL_RETAIL_OFFICE_TITLE')
      : t('CONSULTATION_TYPE_LARGE_COMMERCIAL_INDUSTRIAL_TITLE');

  const CONSULTATION_TYPE = {
    [ConsultationType.Email]: {
      label: t('EMAIL'),
      icon: <EmailIcon />,
    },
    [ConsultationType.PhoneCall]: {
      label: t('PHONE_CALL'),
      icon: <PhoneIcon />,
    },
    [ConsultationType.SiteVisit]: {
      label: t('SITE_VISIT'),
      icon: <SiteVisitIcon />,
    },
  };

  const COMMERCIAL_RETAIL_OFFICE: Record<
    CommercialRetailOfficeOption,
    string
  > = {
    [CommercialRetailOfficeOption.EvChargers]: t(
      'COMMERCIAL_RETAIL_OFFICE_EV_CHARGERS',
    ),
    [CommercialRetailOfficeOption.LightingUpgrades]: t(
      'COMMERCIAL_RETAIL_OFFICE_LIGHTING_UPGRADES',
    ),
    [CommercialRetailOfficeOption.RenewablePower]: t(
      'COMMERCIAL_RETAIL_OFFICE_RENEWABLE_POWER',
    ),
    [CommercialRetailOfficeOption.SmartThermostat]: t(
      'COMMERCIAL_RETAIL_OFFICE_SMART_THERMOSTAT',
    ),
    [CommercialRetailOfficeOption.Other]: t('OTHER'),
  };

  const LARGE_COMMERCIAL_INDUSTRIAL: Record<
    LargeCommercialIndustrialOption,
    string
  > = {
    [LargeCommercialIndustrialOption.AnalyticsTools]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_ANALYTICS_TOOLS',
    ),
    [LargeCommercialIndustrialOption.EnergyShifting]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_ENERGY_SHIFTING',
    ),
    [LargeCommercialIndustrialOption.EvChargers]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_EV_CHARGERS',
    ),
    [LargeCommercialIndustrialOption.OnsiteGenerationBattery]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_ONSITE_GENERATION_BATTERY',
    ),
    [LargeCommercialIndustrialOption.RenewablePower]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_RENEWABLE_POWER',
    ),
    [LargeCommercialIndustrialOption.SmartThermostat]: t(
      'LARGE_COMMERCIAL_INDUSTRIAL_SMART_THERMOSTAT',
    ),
    [LargeCommercialIndustrialOption.Other]: t('OTHER'),
  };

  return (
    <Paper className={classes.card}>
      <div className={classes.header}>
        <Box className={classes.iconContainer}>
          {
            CONSULTATION_TYPE[consultation?.requestDetails?.consultationType]
              ?.icon
          }
        </Box>
        <Typography className={classes.title}>
          {
            CONSULTATION_TYPE[consultation?.requestDetails?.consultationType]
              ?.label
          }{' '}
          - {businessNeedsCategory}
        </Typography>
      </div>

      <div className={classes.row}>
        {consultation?.requestDetails?.consultationType ===
          ConsultationType.SiteVisit && (
          <>
            <div>
              <Typography className={classes.label}>{t('DATE')}</Typography>
              <Typography className={classes.value}>
                {consultation.requestDetails?.preferredDate
                  ? format(
                      parseISO(
                        consultation.requestDetails?.preferredDate ?? '',
                      ),
                      'MMMM d, yyyy',
                      language === SupportedLanguages.Spanish
                        ? { locale: es }
                        : undefined,
                    )
                  : ''}
              </Typography>
            </div>

            <div>
              <Typography className={classes.label}>{t('TIME')}</Typography>
              <Typography className={classes.value}>
                {consultation.requestDetails?.preferredTimeSlot}
              </Typography>
            </div>
          </>
        )}
        <div>
          <Typography className={classes.label}>{t('TYPE')}</Typography>
          <Typography className={classes.value}>
            {
              CONSULTATION_TYPE[consultation?.requestDetails?.consultationType]
                ?.label
            }
          </Typography>
        </div>

        <div>
          <Typography className={classes.label}>{t('ACCOUNT')}</Typography>
          <Typography className={classes.value}>
            {consultation.requestDetails?.businessName}
          </Typography>
        </div>
      </div>

      <div className={classes.energyNeeds}>
        <Typography className={classes.label}>{t('ENERGY_NEEDS')}</Typography>
        <Typography className={classes.value}>
          {consultation.requestDetails?.commercialRetailOfficeOptions
            ?.map(o => COMMERCIAL_RETAIL_OFFICE[o])
            ?.join(', ') ||
            consultation.requestDetails?.largeCommercialIndustrialOptions
              ?.map(o => LARGE_COMMERCIAL_INDUSTRIAL[o])
              ?.join(', ')}
          {consultation.requestDetails?.otherBusinessNeed
            ? `, ${consultation.requestDetails?.otherBusinessNeed}`
            : ''}
        </Typography>
      </div>

      <div className={classes.actions}>
        {consultation?.requestDetails?.consultationType ===
          ConsultationType.SiteVisit && (
          <Button
            variant="contained"
            className={classes.rescheduleBtn}
            onClick={() => onReschedule?.(consultation.requestId)}
          >
            {t('RESCHEDULE')}
          </Button>
        )}

        <Button
          variant="outlined"
          className={classes.cancelBtn}
          onClick={onCancel}
        >
          {t('CANCEL_REQUEST')}
        </Button>
      </div>
    </Paper>
  );
};

export default ConsultationRequestCard;
